import {Button, Hotkey, InfoTooltip} from '@wandb/components';
import {isMac} from '@wandb/weave/common/util/browser';
import * as Dialog from '@wandb/weave/components/Dialog';
import React, {useCallback, useMemo, useState} from 'react';

import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/SemanticLegendDefaults';
import {useSemanticLegendSettingsContext} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsContextProvider';
import {parseBucketsFromSemanticLegendSettings} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/utils/parseBuckets';
import {
  SemanticLegendsQueryDocument,
  useNamedWorkspaceUpdateViewFieldsLazyQuery,
  useUpdateSemanticLegendMutation,
  useUpdateSemanticLegendWithCategoricalConfigMutation,
} from '../../../generated/graphql';
import {namedWorkspaceBelongsToUser} from '../../../state/workspaces/types';
import {HOTKEYS, HOTKEYS_FALLBACK} from '../../../util/hotkeys';
import {useRampFlagSemanticLegendsCategoricalConfig} from '../../../util/rampFeatureFlags';
import {SemanticLegendSettingsWithCategoricalConfig} from '../../WorkspaceDrawer/Settings/types';
import {
  useViewBarActionContext,
  useViewBarContext,
} from '../NamedWorkspaceViewBarContext';
import {
  getDisabledTooltipContent,
  parseAndTrackNamedWorkspaces,
} from '../utils';
import {
  getLocalStorageShouldDisableSaveViewModal,
  getShouldShowSaveWarning,
} from './modals/SaveViewModalContent';

const saveHotkeys = isMac
  ? HOTKEYS.saveWorkspace
  : HOTKEYS_FALLBACK.saveWorkspace;

export const ViewBarButton = () => {
  const {workspaceType, viewer, shouldRevokeWritePerms} = useViewBarContext();
  const {copy, save} = useSaveAndCopyActions();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // there is no action if you're not logged in
  if (workspaceType === 'unknown' || viewer?.username == null) {
    return null;
  }

  return (
    <div className="hidden gap-8 xl:contents">
      {save.isDisplayed && (
        <>
          <Dialog.Trigger asChild>
            <Button
              data-test="nw-save-view"
              isDisabled={save.isUiDisabled}
              variant="secondary"
              onPress={() => save.fn(false)}
              tooltip={{
                children: <Hotkey keys={saveHotkeys} name="Save view" />,
                placement: 'bottom',
              }}>
              Save view
            </Button>
          </Dialog.Trigger>
          {shouldRevokeWritePerms && (
            <InfoTooltip
              id="save-view-info-tooltip"
              aria-label={getDisabledTooltipContent(viewer)}>
              {getDisabledTooltipContent(viewer)}
            </InfoTooltip>
          )}
        </>
      )}
      {/* Don't show this button if personal workspace belongs to the user */}
      {copy.isDisplayed && (
        <Button data-test="nw-copy-to-personal" onClick={copy.fn}>
          Copy to my workspace
        </Button>
      )}
    </div>
  );
};

export const useSaveAndCopyActions = () => {
  const {
    workspaceType,
    viewer,
    copyToPersonalWorkspace,
    saveView,
    currentView,
    currentWorkspaceNwId,
    shouldRevokeWritePerms,
    hasUnsavedChanges,
    personalWorkspaces,
  } = useViewBarContext();
  const {setModalDetails} = useViewBarActionContext();
  const {semanticLegendSettings, isMetricBasedColors} =
    useSemanticLegendSettingsContext();
  const [updateSemanticLegendSettingsOld] = useUpdateSemanticLegendMutation();
  const [updateSemanticLegendSettingsWithCategoricalConfig] =
    useUpdateSemanticLegendWithCategoricalConfigMutation();
  const isCategoricalConfigEnabled =
    useRampFlagSemanticLegendsCategoricalConfig(
      currentView?.project?.entityName ?? ''
    );
  const updateSemanticLegendSettings = useMemo(
    () =>
      isCategoricalConfigEnabled
        ? updateSemanticLegendSettingsWithCategoricalConfig
        : updateSemanticLegendSettingsOld,
    [
      isCategoricalConfigEnabled,
      updateSemanticLegendSettingsWithCategoricalConfig,
      updateSemanticLegendSettingsOld,
    ]
  );
  const viewerUsername = viewer?.username;

  // Go grab the latest updatedValue from db to determine if there is a potential conflict
  const [getUpdateViewFields, {loading, data: latestData}] =
    useNamedWorkspaceUpdateViewFieldsLazyQuery({
      fetchPolicy: 'network-only',
    });

  const isPersonalWorkspace = workspaceType === 'personal';
  const isOwnWorkspace = namedWorkspaceBelongsToUser(
    currentWorkspaceNwId,
    viewer
  );

  const isCopyDisplayed = copyToPersonalWorkspace != null && !isOwnWorkspace;

  const isSaveDisplayed = !isPersonalWorkspace;
  const isSaveHotkeyDisabled =
    saveView == null ||
    loading ||
    shouldRevokeWritePerms ||
    currentView?.saving ||
    (isPersonalWorkspace && !isOwnWorkspace);
  // We allow saving with hotkeys even if there are no unsaved changes so there's
  // always feedback when the user presses the keys. This ensures it doesn't feel
  // like the feature is broken or the keys are ignored.
  // However, the UI controls (e.g. button, menu item) use a stricter check to
  // prevent users from triggering unnecessary saves when there are no changes.
  const isSaveUiDisabled = isSaveHotkeyDisabled || !hasUnsavedChanges;

  const copy = useCallback(() => {
    parseAndTrackNamedWorkspaces({
      action: 'Copy to personal workspace',
      location: 'view bar',
      currentView,
      viewer,
    });
    copyToPersonalWorkspace?.();

    // if we have a semantic legend, copy it to the personal workspace
    if (semanticLegendSettings && isMetricBasedColors) {
      const {customColors, bucketUpperBounds} =
        parseBucketsFromSemanticLegendSettings(semanticLegendSettings);
      const personalWorkspace = personalWorkspaces[viewerUsername ?? ''];
      if (personalWorkspace) {
        updateSemanticLegendSettings({
          variables: {
            id: personalWorkspace.id ?? '',
            currentMetric: semanticLegendSettings?.key ?? '',
            metricName: semanticLegendSettings?.key ?? '',
            yValue:
              semanticLegendSettings?.yAxisValue ??
              DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
            customColors: customColors?.map(color => color ?? ''),
            bucketUpperBounds: bucketUpperBounds?.map(bucket => bucket ?? 0),
            excludedRunColor: semanticLegendSettings?.excludedRunColor,
            categories: isCategoricalConfigEnabled
              ? (
                  semanticLegendSettings as SemanticLegendSettingsWithCategoricalConfig
                )?.selectedCategories // we know it's this type because the ramp flag passes
              : undefined,
          },
          refetchQueries: [
            {
              query: SemanticLegendsQueryDocument,
              variables: {id: personalWorkspace.id ?? ''},
            },
          ],
        });
      }
    }
  }, [
    copyToPersonalWorkspace,
    currentView,
    viewer,
    semanticLegendSettings,
    updateSemanticLegendSettings,
    personalWorkspaces,
    viewerUsername,
    isMetricBasedColors,
    isCategoricalConfigEnabled,
  ]);

  const save = useCallback(
    (isKeyboardShortcut: boolean) => {
      if (currentView?.id == null) {
        return;
      }

      if (isSaveHotkeyDisabled) {
        return;
      }

      getUpdateViewFields({variables: {id: currentView.id}});
      const shouldShowSaveWarning = getShouldShowSaveWarning(
        latestData,
        currentView
      );

      if (
        shouldShowSaveWarning ||
        !getLocalStorageShouldDisableSaveViewModal()
      ) {
        // Always show the save modal if there's a save conflict
        setModalDetails({
          type: 'save view',
        });
        return;
      }
      parseAndTrackNamedWorkspaces({
        action: 'Save view',
        location: isKeyboardShortcut ? 'keyboard shortcut' : 'view bar',
        currentView,
        viewer,
      });
      saveView?.();
    },
    [
      currentView,
      getUpdateViewFields,
      isSaveHotkeyDisabled,
      latestData,
      saveView,
      setModalDetails,
      viewer,
    ]
  );

  return {
    copy: {
      fn: copy,
      isDisplayed: isCopyDisplayed,
    },
    save: {
      fn: save,
      isDisplayed: isSaveDisplayed,
      isHotkeyDisabled: isSaveHotkeyDisabled,
      isUiDisabled: isSaveUiDisabled,
    },
  };
};
